import { useRef } from 'react';
import { usePerspectiveCropperInteraction } from './usePerspectiveCropperInteraction';
import { usePerspectiveCropperRender } from './usePerspectiveCropperRender';

type PerspectiveCropperStateChange = { loading?: boolean };

type UsePerspectiveCropperLogicParams = {
  imageUrl: string;
  file: File;
  onChange?: (state: PerspectiveCropperStateChange) => void;
  onDragStop?: (state: PerspectiveCropperStateChange) => void;
};

type UsePerspectiveCropperLogicResult = {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  previewCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  overlayRef: React.MutableRefObject<HTMLDivElement | null>;
  loading: boolean;
  frameSize: { width: number; height: number } | null;
  handlePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerLeave: (event: React.PointerEvent<HTMLDivElement>) => void;
  backToCrop: () => void;
  done: () => Promise<Blob>;
  setHorizontalTilt: (value: number) => void;
  setVerticalTilt: (value: number) => void;
  setRotation: (value: number) => void;
};

export const usePerspectiveCropperLogic = ({
  imageUrl,
  file,
  onChange,
  onDragStop,
}: UsePerspectiveCropperLogicParams): UsePerspectiveCropperLogicResult => {
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const {
    containerRef,
    previewCanvasRef,
    loading,
    naturalSize,
    zoomRef,
    panRef,
    clampPan,
    getCropRect,
    renderPreview,
    backToCrop,
    done,
    setHorizontalTilt,
    setVerticalTilt,
    setRotation,
  } = usePerspectiveCropperRender({ imageUrl, file, onChange });

  const { frameSize, handlePointerDown, handlePointerMove, handlePointerUp, handlePointerLeave } =
    usePerspectiveCropperInteraction({
      containerRef,
      previewCanvasRef,
      overlayRef,
      naturalSize,
      zoomRef,
      panRef,
      getCropRect,
      clampPan,
      renderPreview,
      onDragStop,
    });

  return {
    containerRef,
    previewCanvasRef,
    overlayRef,
    loading,
    frameSize,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerLeave,
    backToCrop,
    done,
    setHorizontalTilt,
    setVerticalTilt,
    setRotation,
  };
};
